import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ImageUpload } from "@/components/ImageUpload";
import { RichTextEditor } from "@/components/RichTextEditor";
import { AYRSHIRE_TOWNS } from "@/hooks/use-town";
import { useCategories } from "@/hooks/use-categories";

const TOWNS = AYRSHIRE_TOWNS.filter(t => t !== "All Towns");

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function Toggle({ value, onChange, label, color = "bg-primary" }: { value: boolean; onChange: (v: boolean) => void; label: string; color?: string }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`w-10 h-5 rounded-full transition-colors relative ${value ? color : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </label>
  );
}

export default function AdminNewsCreate() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const isJournalist = (user as { isJournalist?: boolean } | null)?.isJournalist === true;

  const newsCategories = useCategories("news");
  const [form, setForm] = useState({
    title: "",
    summary: "",
    content: "",
    byline: "",
    category: "",
    town: "Ayr",
    imageUrl: "",
    sourceName: "",
    sourceLink: "",
    featured: false,
    breaking: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!form.category && newsCategories.length > 0) setForm(f => ({ ...f, category: newsCategories[0]! }));
  }, [newsCategories]);

  // Pre-fill byline with reporter's name
  useEffect(() => {
    if (user && !form.byline) {
      const name = (user as { name?: string }).name ?? "";
      if (name) setForm(f => ({ ...f, byline: name }));
    }
  }, [user]);

  if (authLoading) return null;
  if (user?.role !== "admin" && !isJournalist) return <Redirect to="/" />;

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.summary.trim()) {
      toast({ title: "Title and summary are required", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/news", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          title: form.title,
          summary: form.summary,
          content: form.content || form.summary,
          byline: form.byline,
          category: form.category,
          town: form.town,
          imageUrl: form.imageUrl,
          sourceName: form.sourceName,
          sourceLink: form.sourceLink,
          featured: form.featured,
          breaking: form.breaking,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? "Failed");
      }
      toast({ title: "Article published!", description: "Your news article is now live." });
      setLocation("/news");
    } catch (err) {
      toast({ title: "Failed to publish", description: err instanceof Error ? err.message : "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout current="/admin/news/create">
      <form onSubmit={e => void handleSubmit(e)} className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-1">
            {isJournalist ? "Write News Article" : "Post News Article"}
          </h1>
          <p className="text-sm text-slate-500">
            {isJournalist
              ? "Your article will be published immediately with your name as the byline."
              : "Publish a news article directly to the Ayrshire Hub platform."}
          </p>
        </div>

        {/* Article Info */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Article Details</h2>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Headline <span className="text-red-500">*</span>
            </label>
            <input
              required
              value={form.title}
              onChange={e => set("title", e.target.value)}
              placeholder="Write a compelling headline…"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40 transition-colors font-medium"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
              <select
                value={form.category}
                onChange={e => set("category", e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40 bg-white"
              >
                {newsCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Town / Area</label>
              <select
                value={form.town}
                onChange={e => set("town", e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40 bg-white"
              >
                {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Reporter / Byline
            </label>
            <input
              value={form.byline}
              onChange={e => set("byline", e.target.value)}
              placeholder="e.g. Jane Smith — appears under the headline"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40 transition-colors"
            />
            <p className="text-xs text-slate-400 mt-1">This name appears as the article byline. Pre-filled with your account name.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Summary / Lead <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={form.summary}
              onChange={e => set("summary", e.target.value)}
              placeholder="A one or two sentence summary shown in the news feed and used for social sharing…"
              rows={3}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40 transition-colors resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">This appears as the article preview and is used for Facebook/Twitter sharing descriptions.</p>
          </div>
        </section>

        {/* Rich text body */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Article Body</h2>
              <p className="text-xs text-slate-400 mt-0.5">Use headings, subheadings, bullet points and blockquotes to structure your article.</p>
            </div>
          </div>
          <RichTextEditor
            value={form.content}
            onChange={v => set("content", v)}
            placeholder="Write your full article here — use H2 for section headings, blockquotes for notable quotes…"
          />
          <p className="text-xs text-slate-400">Leave blank to use the summary as the full article body.</p>
        </section>

        {/* Media & Source */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Media & Source</h2>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Cover Image</label>
            <ImageUpload
              value={form.imageUrl}
              onChange={url => set("imageUrl", url)}
              enableCrop
              aspect={16 / 9}
            />
            <p className="text-xs text-slate-400 mt-1">This image appears at the top of the article and in social sharing previews on Facebook.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Source Name</label>
              <input
                value={form.sourceName}
                onChange={e => set("sourceName", e.target.value)}
                placeholder="e.g. Ayrshire Post"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Source Link</label>
              <input
                type="url"
                value={form.sourceLink}
                onChange={e => set("sourceLink", e.target.value)}
                placeholder="https://…"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40"
              />
            </div>
          </div>
        </section>

        {/* Visibility */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Visibility</h2>
          <div className="flex flex-wrap gap-6">
            <Toggle value={form.featured} onChange={v => set("featured", v)} label="Featured story" />
            <Toggle value={form.breaking} onChange={v => set("breaking", v)} label="Breaking news" color="bg-red-500" />
          </div>
        </section>

        {/* Facebook sharing info */}
        <section className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <span className="text-blue-500 mt-0.5">ℹ</span>
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Facebook sharing</p>
              <p className="text-xs text-blue-600 mt-0.5">
                After publishing, use the <strong>Share link</strong> button on your article page to copy a URL that shows a proper preview (title, image, summary) when posted to Facebook or WhatsApp.
              </p>
            </div>
          </div>
        </section>

        <div className="flex gap-3 pb-8">
          <button
            type="submit"
            disabled={submitting}
            className="bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold px-8 py-2.5 rounded-xl text-sm transition-colors"
          >
            {submitting ? "Publishing…" : "Publish Article"}
          </button>
          <button
            type="button"
            onClick={() => setLocation("/admin")}
            className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
